// apps/office/src/pages/office/PropertyEnquiries.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Phone, Mail, MapPin, DollarSign, Eye,
  CheckCircle, Clock, XCircle, AlertCircle,
  RefreshCw, Loader2, Building2, User,
  Search, Filter, Calendar, MessageCircle,
  Download, Trash2, ChevronDown, ChevronUp,
  ArrowUpDown, TrendingUp, Users, Home
} from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const PropertyEnquiries = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enquiryToDelete, setEnquiryToDelete] = useState(null);

  // Fetch all property enquiries with expanded property data
  const { data: enquiries = [], isLoading, refetch } = useQuery({
    queryKey: ['propertyEnquiries', filterStatus],
    queryFn: async () => {
      try {
        let filter = '';
        if (filterStatus !== 'all') {
          filter = `status = "${filterStatus}"`;
        }
        const records = await pb.collection('property_enquiries').getFullList({
          expand: 'property_id',
          sort: sortOrder === 'desc' ? '-created' : 'created',
          filter: filter,
        });
        return records;
      } catch (error) {
        console.error('Error fetching enquiries:', error);
        toast.error('Failed to load enquiries');
        return [];
      }
    },
    enabled: !!user,
  });

  // Update enquiry status
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) =>
      pb.collection('property_enquiries').update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries(['propertyEnquiries']);
      toast.success('Status updated successfully');
    },
    onError: (error) => {
      console.error('Status update error:', error);
      toast.error('Failed to update status: ' + (error.message || 'Unknown error'));
    },
  });

  // Delete enquiry
  const deleteMutation = useMutation({
    mutationFn: (id) => pb.collection('property_enquiries').delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['propertyEnquiries']);
      toast.success('Enquiry deleted');
      setDeleteDialogOpen(false);
      setEnquiryToDelete(null);
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Failed to delete: ' + (error.message || 'Unknown error'));
    },
  });

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'new': { icon: Clock, label: 'New', color: 'bg-blue-100 text-blue-700 border-blue-200' },
      'contacted': { icon: Phone, label: 'Contacted', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      'interested': { icon: CheckCircle, label: 'Interested', color: 'bg-green-100 text-green-700 border-green-200' },
      'not_interested': { icon: XCircle, label: 'Not Interested', color: 'bg-red-100 text-red-700 border-red-200' },
      'closed': { icon: CheckCircle, label: 'Closed', color: 'bg-gray-100 text-gray-700 border-gray-200' },
    };
    const config = statusMap[status] || statusMap['new'];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Get status options
  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'interested', label: 'Interested' },
    { value: 'not_interested', label: 'Not Interested' },
    { value: 'closed', label: 'Closed' },
  ];

  const handleStatusChange = (id, newStatus) => {
    updateStatusMutation.mutate({ id, status: newStatus });
  };

  const handleDeleteClick = (enquiry) => {
    setEnquiryToDelete(enquiry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (enquiryToDelete) {
      deleteMutation.mutate(enquiryToDelete.id);
    }
  };

  const handleViewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setIsDetailOpen(true);
  };

  // Filter enquiries
  const filteredEnquiries = enquiries.filter((e) => {
    const search = searchTerm.toLowerCase();
    return (
      e.name?.toLowerCase().includes(search) ||
      e.phone?.toLowerCase().includes(search) ||
      e.email?.toLowerCase().includes(search) ||
      e.property_title?.toLowerCase().includes(search) ||
      e.expand?.property_id?.title?.toLowerCase().includes(search)
    );
  });

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format time
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate stats
  const stats = {
    total: enquiries.length,
    new: enquiries.filter(e => e.status === 'new').length,
    contacted: enquiries.filter(e => e.status === 'contacted').length,
    interested: enquiries.filter(e => e.status === 'interested').length,
    closed: enquiries.filter(e => e.status === 'closed').length,
    notInterested: enquiries.filter(e => e.status === 'not_interested').length,
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Name', 'Phone', 'Email', 'Property', 'Budget', 'Status', 'Date'];
    const rows = filteredEnquiries.map(e => [
      e.name || '',
      e.phone || '',
      e.email || '',
      e.expand?.property_id?.title || e.property_title || '',
      e.budget || '',
      e.status || 'new',
      new Date(e.created).toLocaleString('en-IN'),
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enquiries-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported successfully');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-indigo-600" />
            Property Enquiries
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track and manage all property enquiries from clients
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportToCSV}
            className="gap-2"
            disabled={filteredEnquiries.length === 0}
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <Users className="w-3 h-3" /> Total
            </p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{stats.new}</div>
            <p className="text-sm text-blue-600 flex items-center gap-1">
              <Clock className="w-3 h-3" /> New
            </p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.contacted}</div>
            <p className="text-sm text-yellow-600 flex items-center gap-1">
              <Phone className="w-3 h-3" /> Contacted
            </p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{stats.interested}</div>
            <p className="text-sm text-green-600 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Interested
            </p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-gray-50/50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-gray-600">{stats.closed}</div>
            <p className="text-sm text-gray-600 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Closed
            </p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-red-600">{stats.notInterested}</div>
            <p className="text-sm text-red-600 flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Not Interested
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, phone, email or property..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="gap-1 h-10"
            >
              <ArrowUpDown className="w-4 h-4" />
              {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
            </Button>

            {(searchTerm || filterStatus !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                }}
                className="text-red-500 hover:text-red-700"
              >
                <XCircle className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex justify-between items-center text-sm text-slate-500">
        <span>Showing {filteredEnquiries.length} enquiries</span>
      </div>

      {/* Enquiries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>All Enquiries</span>
            <Badge variant="outline" className="text-xs">
              {filteredEnquiries.length} results
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredEnquiries.length === 0 ? (
            <div className="text-center py-16">
              <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">No enquiries found</p>
              <p className="text-sm text-slate-400">
                {searchTerm ? 'Try adjusting your search' : 'Enquiries from property listings will appear here'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEnquiries.map((enquiry) => {
                    const propertyTitle = enquiry.expand?.property_id?.title || enquiry.property_title || 'N/A';
                    const isNew = enquiry.status === 'new';

                    return (
                      <TableRow 
                        key={enquiry.id} 
                        className={`hover:bg-slate-50 transition ${isNew ? 'bg-blue-50/30' : ''}`}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            <span className="truncate max-w-[150px]">{propertyTitle}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3 text-slate-400" />
                            {enquiry.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5 text-xs">
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3 text-slate-400" />
                              <span className="font-medium">{enquiry.phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3 text-slate-400" />
                              <span className="truncate max-w-[120px]">{enquiry.email || 'N/A'}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {enquiry.budget ? (
                            <div className="flex items-center gap-1 font-medium text-slate-700">
                              <DollarSign className="w-3 h-3 text-slate-400" />
                              ?{Number(enquiry.budget).toLocaleString('en-IN')}
                            </div>
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(enquiry.status || 'new')}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col text-xs">
                            <span className="text-slate-700">{formatDate(enquiry.created)}</span>
                            <span className="text-slate-400">{formatTime(enquiry.created)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(enquiry)}
                              className="h-8 w-8 p-0"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            <Select
                              value={enquiry.status || 'new'}
                              onValueChange={(value) => handleStatusChange(enquiry.id, value)}
                            >
                              <SelectTrigger className="w-[110px] h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(enquiry)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enquiry Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Enquiry Details
            </DialogTitle>
            <DialogDescription>
              Full information about this property enquiry
            </DialogDescription>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Client Name</p>
                  <p className="text-base font-semibold text-slate-800">{selectedEnquiry.name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Status</p>
                  {getStatusBadge(selectedEnquiry.status || 'new')}
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Email</p>
                  <p className="text-sm text-slate-700">{selectedEnquiry.email || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Phone</p>
                  <p className="text-sm text-slate-700 font-medium">{selectedEnquiry.phone || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Property</p>
                  <p className="text-sm text-slate-700">
                    {selectedEnquiry.expand?.property_id?.title || selectedEnquiry.property_title || 'N/A'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-500">Budget</p>
                  <p className="text-sm text-slate-700 font-medium">
                    {selectedEnquiry.budget ? `?${Number(selectedEnquiry.budget).toLocaleString('en-IN')}` : 'N/A'}
                  </p>
                </div>
                <div className="col-span-2 space-y-1">
                  <p className="text-sm font-medium text-slate-500">Message</p>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {selectedEnquiry.message || 'No message provided'}
                    </p>
                  </div>
                </div>
                <div className="col-span-2 space-y-1">
                  <p className="text-sm font-medium text-slate-500">Submitted On</p>
                  <p className="text-sm text-slate-700">
                    {new Date(selectedEnquiry.created).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
                  Close
                </Button>
                <div className="flex items-center gap-2">
                  <Select
                    value={selectedEnquiry.status || 'new'}
                    onValueChange={(value) => {
                      handleStatusChange(selectedEnquiry.id, value);
                      setSelectedEnquiry({ ...selectedEnquiry, status: value });
                    }}
                  >
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Enquiry
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this enquiry from "{enquiryToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyEnquiries;